import {
  RefObject,
  useContext,
  useEffect,
  useReducer,
  useRef,
  Fragment
} from "react";
import { useIntl } from "react-intl";
import { Assignee, Step, WorkflowTask } from "../ReviewsInProgress";
import { WorkflowTaskStep } from "./WorkflowTaskStep";
import useSmallScreenView from "@/features/common/useSmallScreenView";
import { WorkflowTaskStepSmallScreen } from "./WorkflowTaskStepSmallScreen";
import { WorkflowTaskStepTooltip } from "./WorkflowTaskStepTooltip";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { WmButton } from "@watermarkinsights/ripple-react";
import {
  ARROW_LEFT,
  ARROW_RIGHT,
  ENTER,
  IN_PROGRESS_STATUS,
  LEFT,
  RIGHT,
  STEPS_FOR_EDGE_CASE,
  TAB
} from "@/features/common/constants";
import EdgeCaseArrowButton from "./EdgeCaseArrowButton";
import { WorkFlowTaskFlyout } from "./WorkflowTaskFlyout";
import translate from "@/i18n/translate";

interface WorkFlowTaskProgressProps {
  workflowTask: WorkflowTask;
  totalWorkflows: number;
}

export interface TooltipData {
  name: string;
  submittedDate: string | null;
  dueDate: string | null;
  assignees: Assignee[];
  state: string;
  stepIndex: number;
  totalSteps: number;
  positionX?: number;
  daysUntilDue: number | null;
  carrotPosition?: number;
  dueDateType: string;
  sentBackTo: string;
  skippedTo: string;
  splitStep: boolean;
  id: string;
  submissionUrl: string;
}
interface State {
  showSteps: boolean;
  showTooltip: boolean;
  tooltipData: TooltipData | null;
  showTooltipStepIndex: number | null;
  tooltipDataSmallScreen: null | TooltipData;
  showTooltipSmallScreen: boolean;
  currentStepIndex: number;
  clickedStep: number;
  edgeCaseTriggered: boolean;
  isDrilldownOpen: boolean;
}
interface Action {
  type: string;
  showSteps: boolean;
  showTooltip: boolean;
  tooltipData: TooltipData | null;
  showTooltipStepIndex: number | null;
  showTooltipSmallScreen: boolean;
  tooltipDataSmallScreen: null | TooltipData;
  currentStepIndex: number;
  clickedStep: number;
  edgeCaseTriggered: boolean;
  isDrilldownOpen: boolean;
}

const defaultAction: Action = {
  type: "",
  showSteps: false,
  showTooltip: false,
  tooltipData: null,
  showTooltipStepIndex: null,
  showTooltipSmallScreen: false,
  tooltipDataSmallScreen: null,
  currentStepIndex: -1,
  clickedStep: -1,
  edgeCaseTriggered: false,
  isDrilldownOpen: false
};

const stateReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "toggleSteps":
      return {
        ...state,
        showSteps: !state.showSteps
      };
    case "openTooltip":
      return {
        ...state,
        showTooltip: true
      };
    case "closeTooltip":
      return {
        ...state,
        showTooltip: false,
        clickedStep: -1
      };
    case "setTooltipData": {
      return {
        ...state,
        tooltipData: action.tooltipData
      };
    }
    case "setTooltipIndexSmallScreen": {
      return {
        ...state,
        showTooltipStepIndex: action.showTooltipStepIndex
      };
    }
    case "toggleSmallScreenTooltip": {
      return {
        ...state,
        showTooltipSmallScreen: !state.showTooltipSmallScreen
      };
    }
    case "setTooltipDataSmallScreen": {
      return {
        ...state,
        tooltipDataSmallScreen: action.tooltipDataSmallScreen
      };
    }
    case "setCurrentStepIndex":
      return {
        ...state,
        currentStepIndex: action.currentStepIndex ?? -1
      };
    case "setClickedstepIndex":
      return {
        ...state,
        clickedStep: action.clickedStep ?? -1
      };
    case "setEdgeCaseStepsClicked":
      return {
        ...state,
        edgeCaseTriggered: true
      };
    case "resetEdgeCaseStepsClicked":
      return {
        ...state,
        edgeCaseTriggered: false
      };
    case "ToggleDrilldown":
      return { ...state, isDrilldownOpen: !state.isDrilldownOpen };
    case "CloseDrilldown":
      return { ...state, isDrilldownOpen: false };
    default:
      return state;
  }
};

export function isStepsLessThanEqualToEdgeCase(stepsLength: number) {
  return stepsLength <= STEPS_FOR_EDGE_CASE;
}
export function isStepsMoreThanEdgeCase(stepsLength: number) {
  return stepsLength > STEPS_FOR_EDGE_CASE;
}

export function WorkFlowTaskProgress({
  workflowTask,
  totalWorkflows
}: WorkFlowTaskProgressProps) {
  const initialState: State = {
    showSteps: totalWorkflows === 1,
    showTooltip: false,
    tooltipData: null,
    showTooltipStepIndex: null,
    tooltipDataSmallScreen: null,
    showTooltipSmallScreen: false,
    currentStepIndex: -1,
    clickedStep: -1,
    edgeCaseTriggered: false,
    isDrilldownOpen: false
  };
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const { sysUser } = useContext<AppContextInterface>(AppContext);
  const isAdminUser = sysUser?.roles.includes("administrator");
  const isSmallScreen = useSmallScreenView(1124);
  const tooltipRef: RefObject<HTMLDivElement> = useRef(null);
  function toggleSteps() {
    dispatch({ ...defaultAction, type: "toggleSteps" });
  }

  const toggleStepsLabel = useIntl().formatMessage({
    id: "app.home.reviewsInProgress.toggleSteps"
  });
  const workflowTaskLabel = useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.workflowTaskLabel" },
    { title: workflowTask.title }
  );
  function showSmallScreenSteps(showSteps: boolean): boolean {
    return isSmallScreen && showSteps;
  }

  function removeTabIndex(
    stepContainer: HTMLElement,
    currentStepIndex: number
  ) {
    if (stepContainer && currentStepIndex !== -1) {
      const previousElement = stepContainer.children[
        currentStepIndex
      ] as HTMLElement;
      previousElement.tabIndex = -1;
    }
  }

  function focusRight(
    totalSteps: number,
    stepContainer: HTMLDivElement,
    currentStepIndex: number,
    event: any
  ) {
    if (totalSteps > 0) {
      dispatch({
        ...defaultAction,
        type: "closeTooltip"
      });
      if (isEdgeCaseTriggeredForFsRptSteps()) {
        dispatch({
          ...defaultAction,
          type: "setEdgeCaseStepsClicked"
        });
      }
      const nextIndex = (currentStepIndex + 1) % totalSteps;
      const nextElement = stepContainer.children[nextIndex] as HTMLDivElement;
      nextElement.tabIndex = 0;
      nextElement.focus();
      dispatch({
        ...defaultAction,
        type: "setClickedstepIndex",
        clickedStep: nextIndex
      });

      setTimeout(() => {
        openTooltip(event, nextIndex, nextElement);
      }, 100);
    }
  }

  function focusLeft(
    totalSteps: number,
    stepContainer: HTMLDivElement,
    currentStepIndex: number,
    event: any
  ) {
    if (totalSteps > 0) {
      dispatch({
        ...defaultAction,
        type: "closeTooltip"
      });
      if (isEdgeCaseTriggeredForFsRptSteps()) {
        dispatch({
          ...defaultAction,
          type: "setEdgeCaseStepsClicked"
        });
      }
      const prevIndex =
        currentStepIndex === -1
          ? totalSteps - 1
          : (currentStepIndex - 1 + totalSteps) % totalSteps;
      const prevElement = stepContainer.children[prevIndex] as HTMLDivElement;

      prevElement.tabIndex = 0;
      prevElement.focus();
      dispatch({
        ...defaultAction,
        type: "setClickedstepIndex",
        clickedStep: prevIndex
      });

      setTimeout(() => {
        openTooltip(event, prevIndex, prevElement);
      }, 100);
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const steps = workflowTask.steps;
    const totalSteps = steps.length;
    const currentStepIndex = state.clickedStep;
    let parent = event.target.closest(".task-progress-container");
    const stepContainer = parent.querySelector(".steps-container");
    removeTabIndex(stepContainer, currentStepIndex);
    switch (event.key) {
      case ARROW_RIGHT:
        event.preventDefault();
        focusRight(totalSteps, stepContainer, currentStepIndex, event);
        break;
      case ARROW_LEFT:
        event.preventDefault();
        focusLeft(totalSteps, stepContainer, currentStepIndex, event);
        break;
      case ENTER:
        event.preventDefault();
        if (isEdgeCaseTriggeredForFsRptSteps()) {
          openEdgeCaseMode();
        }
        break;
      default:
        break;
    }

    if (
      event.key === TAB &&
      event.shiftKey &&
      isStepsLessThanEqualToEdgeCase(workflowTask.steps.length)
    ) {
      dispatch({
        ...defaultAction,
        type: "closeTooltip"
      });
    }
  };

  function expandStepOnClick(stepIndex: number) {
    setTimeout(() => {
      dispatch({
        ...defaultAction,
        type: "setClickedstepIndex",
        clickedStep: stepIndex
      });
    }, 100);
  }

  function openTooltip(
    event: any,
    stepIndex: number,
    focusedElement?: HTMLElement,
    edgeCaseTriggered?: boolean,
    direction?: string
  ) {
    setTimeout(() => {
      const selectedStep = workflowTask.steps.find(
        (step) => step.stepIndex === stepIndex
      );
      let parent = event.target.closest(".task-progress-container");
      const stepContainer = parent && parent.querySelector(".steps-container");
      let element = null;
      if (direction || (event.key && focusedElement)) {
        element = focusedElement;
      } else {
        edgeCaseTriggered
          ? (element = stepContainer.children[stepIndex] as HTMLDivElement)
          : (element = event.target.closest(".step"));
      }
      if (edgeCaseTriggered && event.key === ENTER) {
        element.tabIndex = 0;
        element.focus();
      }
      let rect = element.getBoundingClientRect();
      const parentElement = element.parentNode;
      let parentElementRect = parentElement.getBoundingClientRect();
      let left = isAdminUser
        ? rect.left - 355 + rect.width / 2
        : rect.left - 310 + rect.width / 2;
      let carrotPositionX = isAdminUser
        ? rect.left - 181 + rect.width / 2
        : rect.left - 130 + rect.width / 2;
      if (left < 0) {
        left = 0;
      }
      if (left > parentElementRect.width - 363) {
        left = parentElementRect.width - 363;
      }
      if (selectedStep && element) {
        const {
          submittedDate,
          assignees,
          dueDate,
          state,
          stepIndex,
          name,
          daysUntilDue,
          dueDateType,
          sentBackTo,
          skippedTo,
          splitStep,
          id,
          submissionUrl
        } = selectedStep;
        const tooltipData = {
          totalSteps: workflowTask.steps.length,
          name,
          stepIndex,
          submittedDate,
          assignees,
          dueDate,
          state,
          positionX: left < 0 ? 0 : left,
          daysUntilDue,
          carrotPosition: carrotPositionX,
          dueDateType,
          sentBackTo,
          skippedTo,
          splitStep,
          id,
          submissionUrl
        };
        dispatch({ ...defaultAction, type: "openTooltip" });
        dispatch({
          ...defaultAction,
          type: "setTooltipData",
          tooltipData
        });
      }
    }, 200);
  }

  function openTooltipSmallScreen(index: number) {
    if (state.showTooltipStepIndex === index) {
      dispatch({
        ...defaultAction,
        type: "setTooltipIndexSmallScreen",
        showTooltipStepIndex: null
      });
    } else {
      const selectedStep = workflowTask.steps.find(
        (step) => step.stepIndex === index
      );
      if (selectedStep) {
        const {
          submittedDate,
          assignees,
          dueDate,
          state,
          stepIndex,
          name,
          daysUntilDue,
          dueDateType,
          sentBackTo,
          skippedTo,
          splitStep,
          id,
          submissionUrl
        } = selectedStep;
        const tooltipData = {
          totalSteps: workflowTask.totalSteps,
          name,
          stepIndex,
          submittedDate,
          assignees,
          dueDate,
          state,
          daysUntilDue,
          dueDateType,
          sentBackTo,
          skippedTo,
          splitStep,
          id,
          submissionUrl
        };
        setTimeout(() => {
          dispatch({
            ...defaultAction,
            type: "setTooltipIndexSmallScreen",
            showTooltipStepIndex: stepIndex
          });
        }, 300);
        dispatch({
          ...defaultAction,
          type: "setTooltipDataSmallScreen",
          tooltipDataSmallScreen: tooltipData
        });
      }
    }
  }

  function checkIfClickedElementIsStep(element: HTMLElement) {
    const stepContainer = tooltipRef.current
      ?.closest(".task-progress-container")
      ?.querySelector(".steps-container");
    const stepsArray = Array.from(stepContainer?.children || []);
    return stepsArray.some((step) => step.contains(element));
  }

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !checkIfClickedElementIsStep(event.target) &&
        !event.target.closest(".right-button") &&
        !event.target.closest(".left-button")
      ) {
        setTimeout(() => {
          dispatch({ ...defaultAction, type: "closeTooltip" });
          isStepsMoreThanEdgeCase(workflowTask.steps.length) &&
            dispatch({ ...defaultAction, type: "resetEdgeCaseStepsClicked" });
        }, 300);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function handleStepsClickedEdgeCase(event) {
    if (isStepsMoreThanEdgeCase(workflowTask.steps.length)) {
      event?.preventDefault();
      openEdgeCaseMode();
    } else {
      event?.preventDefault();
    }
  }

  function openEdgeCaseMode() {
    const inProgressStep = workflowTask.steps.find(
      (step) => step.state === IN_PROGRESS_STATUS
    );
    dispatch({
      ...defaultAction,
      type: "setEdgeCaseStepsClicked"
    });
    if (inProgressStep) {
      dispatch({
        ...defaultAction,
        type: "setClickedstepIndex",
        clickedStep: inProgressStep?.stepIndex
      });
      openTooltip(event, inProgressStep?.stepIndex, undefined, true);
    }
  }

  function isEdgeCaseTriggeredForFsRptSteps() {
    return (
      workflowTask.steps.length > STEPS_FOR_EDGE_CASE &&
      !state.edgeCaseTriggered
    );
  }

  const setIsDrilldownOpen = () => {
    dispatch({ ...defaultAction, type: "ToggleDrilldown" });
  };

  const openDrilldownView = () => {
    setIsDrilldownOpen();
  };

  const closeDrilldownView = () => {
    dispatch({ ...defaultAction, type: "CloseDrilldown" });
  };

  return (
    <div
      className={
        totalWorkflows > 1 && !state.showSteps
          ? "task-progress-container tabbing  reduced-padding "
          : "task-progress-container tabbing workflowtasks-steps-table"
      }
      tabIndex={0}
      aria-label={workflowTaskLabel}
      onKeyDown={(event) => handleKeyDown(event)}
      onFocus={() => {
        if (
          !state.showTooltip &&
          isStepsLessThanEqualToEdgeCase(workflowTask.steps.length)
        ) {
          dispatch({
            ...defaultAction,
            type: "setClickedstepIndex",
            clickedStep: -1
          });
        }
      }}
    >
      <div className="task-name">
        {totalWorkflows > 1 && (
          <button
            className={
              state.showSteps
                ? "expand-button mdi  mdi-menu-down"
                : "expand-button mdi  mdi-menu-right"
            }
            onClick={toggleSteps}
            aria-label={toggleStepsLabel}
            onBlur={() => dispatch({ ...defaultAction, type: "closeTooltip" })}
          ></button>
        )}
        {workflowTask.title}
      </div>
      {state.edgeCaseTriggered && (
        <EdgeCaseArrowButton
          closeTooltip={() => {
            dispatch({ ...defaultAction, type: "closeTooltip" });
          }}
          workflowTask={workflowTask}
          direction={LEFT}
          clickedStep={state.clickedStep}
          openTooltip={openTooltip}
          setClickedStep={(stepIndex: number) =>
            dispatch({
              ...defaultAction,
              type: "setClickedstepIndex",
              clickedStep: stepIndex
            })
          }
        />
      )}
      {!isSmallScreen && (
        <div
          className={
            state.edgeCaseTriggered
              ? "steps-container arrow-margin"
              : "steps-container"
          }
          onClick={(event) => handleStepsClickedEdgeCase(event)}
        >
          {workflowTask.steps.map((step: Step) => {
            return (
              <WorkflowTaskStep
                key={step.id}
                step={step}
                allExpanded={workflowTask.steps.length <= 4}
                openTooltip={openTooltip}
                clickedStep={state.clickedStep}
                expandStepOnClick={expandStepOnClick}
                stepsLength={workflowTask.steps.length}
              />
            );
          })}
        </div>
      )}
      {state.edgeCaseTriggered && (
        <EdgeCaseArrowButton
          closeTooltip={() => {
            dispatch({ ...defaultAction, type: "closeTooltip" });
          }}
          workflowTask={workflowTask}
          direction={RIGHT}
          clickedStep={state.clickedStep}
          openTooltip={openTooltip}
          setClickedStep={(stepIndex: number) =>
            dispatch({
              ...defaultAction,
              type: "setClickedstepIndex",
              clickedStep: stepIndex
            })
          }
        />
      )}
      {state.showTooltip && state.tooltipData && (
        <div className="step-tooltip">
          <WorkflowTaskStepTooltip
            tooltipData={state.tooltipData}
            tooltipRef={tooltipRef}
            closeTooltip={() => {
              dispatch({ ...defaultAction, type: "closeTooltip" });
            }}
            closeEdgeCaseTriggered={() => {
              dispatch({ ...defaultAction, type: "resetEdgeCaseStepsClicked" });
            }}
            stepsLength={workflowTask.steps.length}
          />
        </div>
      )}

      {showSmallScreenSteps(state.showSteps) && (
        <div className="steps-container">
          {workflowTask.steps.map((step: Step) => {
            return (
              <WorkflowTaskStepSmallScreen
                key={`ss-${step.id}`}
                step={step}
                showStepIndex={state.showTooltipStepIndex ?? -1}
                tooltipData={state.tooltipDataSmallScreen}
                showTooltipSmallScreen={state.showTooltipSmallScreen}
                openTooltip={openTooltipSmallScreen}
              />
            );
          })}
        </div>
      )}
      <div className="view-more">
        <WmButton buttonType="textonly" onClick={() => openDrilldownView()}>
          <div className="view-all-steps-link">
            {translate("app.home.WorkflowTaskProgress.steps")}
          </div>
        </WmButton>
      </div>
      <Fragment>
        <WorkFlowTaskFlyout
          CloseDrilldownView={closeDrilldownView}
          workflowTask={workflowTask}
          open={state.isDrilldownOpen}
        ></WorkFlowTaskFlyout>
      </Fragment>
    </div>
  );
}
